import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";

function embedUrl(url) {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1`;
  return null;
}

export default function VisualLightbox({ visuals, index, onClose, onNavigate }) {
  const visual = visuals[index];
  const [chrome, setChrome] = useState(true);
  const hideTimer = useRef(null);
  const hasFilm = !!visual?.videoUrl;
  const embed = hasFilm ? embedUrl(visual.videoUrl) : null;

  const wake = useCallback(() => {
    setChrome(true);
    clearTimeout(hideTimer.current);
    if (hasFilm) hideTimer.current = setTimeout(() => setChrome(false), 3000);
  }, [hasFilm]);

  useEffect(() => {
    wake();
    return () => clearTimeout(hideTimer.current);
  }, [index, wake]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNavigate((index + 1) % visuals.length);
      if (e.key === "ArrowLeft") onNavigate((index - 1 + visuals.length) % visuals.length);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [index, visuals.length, onClose, onNavigate]);

  if (!visual) return null;

  return (
    <motion.div
      className="fixed inset-0 z-[9997] bg-black flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      onMouseMove={wake}
      onTouchStart={wake}
      data-testid="visual-lightbox"
    >
      {/* Media */}
      <div className="absolute inset-0 flex items-center justify-center">
        {hasFilm ? (
          embed ? (
            <iframe
              key={visual.id}
              src={embed}
              title={visual.title}
              className="w-full h-full md:w-[86%] md:h-[82%]"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              data-testid="lightbox-embed"
            />
          ) : (
            <video
              key={visual.id}
              src={visual.videoUrl}
              controls
              autoPlay
              playsInline
              poster={visual.thumbnail}
              className="w-full h-full md:w-[86%] md:h-[82%] object-contain"
              data-testid="lightbox-video"
            />
          )
        ) : (
          <div className="relative w-full h-full" data-testid="lightbox-placeholder">
            <img src={visual.thumbnail} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-black/50" />
            <div className="mm-vignette" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
              <motion.p
                animate={{ opacity: [0.4, 0.9, 0.4] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="font-mono text-[10px] tracking-[0.5em] text-gold"
              >
                TRANSMISSION PENDING
              </motion.p>
              <p className="font-serif text-3xl md:text-5xl font-light text-white/85 mt-8 max-w-2xl leading-tight">
                This film has not yet been released.
              </p>
              <p className="font-mono text-[10px] tracking-[0.3em] text-white/35 mt-6">
                IT WILL APPEAR HERE THE MOMENT IT ARRIVES.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Chrome */}
      <AnimatePresence>
        {(chrome || !hasFilm) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0 pointer-events-none"
          >
            <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent px-6 md:px-12 pt-6 pb-16 pointer-events-auto">
              <div className="flex items-start justify-between gap-6">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] text-gold">
                    {visual.type.toUpperCase()} · {visual.date}
                  </p>
                  <h2 className="font-serif text-2xl md:text-4xl font-light text-white mt-2">{visual.title}</h2>
                  {visual.relatedRelease && (
                    <Link
                      to={`/music/${visual.relatedRelease}`}
                      data-testid="lightbox-related-release"
                      className="inline-block mt-3 font-mono text-[10px] tracking-[0.25em] text-white/50 hover:text-gold transition-colors"
                    >
                      FROM THE RELEASE →
                    </Link>
                  )}
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  {visual.externalUrl && (
                    <a
                      href={visual.externalUrl}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="lightbox-external"
                      aria-label="Watch externally"
                      className="text-white/60 hover:text-gold transition-colors p-2"
                    >
                      <ExternalLink size={18} />
                    </a>
                  )}
                  <button
                    onClick={onClose}
                    data-testid="lightbox-close"
                    aria-label="Close"
                    className="text-white/70 hover:text-gold transition-colors p-2"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>
            </div>

            {visuals.length > 1 && (
              <>
                <button
                  onClick={() => onNavigate((index - 1 + visuals.length) % visuals.length)}
                  data-testid="lightbox-prev"
                  aria-label="Previous visual"
                  className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 text-white/50 hover:text-gold transition-colors p-3 pointer-events-auto"
                >
                  <ChevronLeft size={30} />
                </button>
                <button
                  onClick={() => onNavigate((index + 1) % visuals.length)}
                  data-testid="lightbox-next"
                  aria-label="Next visual"
                  className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 text-white/50 hover:text-gold transition-colors p-3 pointer-events-auto"
                >
                  <ChevronRight size={30} />
                </button>
              </>
            )}

            <div className="absolute bottom-5 left-0 right-0 text-center pointer-events-none">
              <span className="font-mono text-[9px] tracking-[0.35em] text-white/30">
                {String(index + 1).padStart(2, "0")} / {String(visuals.length).padStart(2, "0")} · ESC TO CLOSE
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
